import { ActionType, createReducer } from "typesafe-actions";
import { NicheFilesError, NicheFilesStateType } from "@containers/NicheFiles/interface";
import { NicheFilesFilter, ORDER_COLUMN_TYPE, ORDER_TYPE } from "@shared/interfaces";
import { produce } from "immer";

import * as actions from "./actions";

const emptySubscription = {
  id: 0,
  county_ids: [],
  state_ids: [],
};

const defaultFilter: NicheFilesFilter = {
  account_id: null,
  page: 1,
  limit: 50,
  search: null,
  order: ORDER_COLUMN_TYPE.CREATED_AT,
  direction: ORDER_TYPE.DESC,
};

const initialState: NicheFilesStateType = {
  files: null,
  file: null,
  countyPrices: null,
  subscriptions: [],
  subscription: { ...emptySubscription },
  subscriptionsLoaded: false,
  filter: { ...defaultFilter },
  errors: {
    [NicheFilesError.getNicheFilesError]: null,
    [NicheFilesError.getNicheFileError]: null,
  },
};

const reducer = createReducer<NicheFilesStateType, ActionType<typeof actions>>(initialState)
  .handleAction(actions.addState, (state, { payload }) =>
    produce(state, (nextState) => {
      if (state.subscription.state_ids.includes(payload)) return;

      nextState.subscription = {
        ...nextState.subscription,
        state_ids: [...nextState.subscription.state_ids, payload],
      };
    }),
  )
  .handleAction(actions.removeState, (state, { payload }) =>
    produce(state, (nextState) => {
      const state_ids = nextState.subscription.state_ids.filter((s) => s !== payload.stateId);
      const stateCounties = payload.counties?.filter((c) => c.state_id === payload.stateId);
      const county_ids = nextState.subscription.county_ids.filter(
        (countyId) => !stateCounties?.find((c) => c.id === countyId),
      );

      nextState.subscription = {
        ...nextState.subscription,
        state_ids,
        county_ids,
      };
    }),
  )
  .handleAction(actions.addCounty, (state, { payload }) =>
    produce(state, (nextState) => {
      if (state.subscription.county_ids.includes(payload)) return;

      nextState.subscription = {
        ...nextState.subscription,
        county_ids: [...nextState.subscription.county_ids, payload],
      };
    }),
  )
  .handleAction(actions.removeCounty, (state, { payload }) =>
    produce(state, (nextState) => {
      nextState.subscription = {
        ...nextState.subscription,
        county_ids: nextState.subscription.county_ids.filter((c) => c !== payload),
      };
    }),
  )
  .handleAction(actions.fetchNicheFilesCountyPrice.success, (state, { payload }) =>
    produce(state, (nextState) => {
      nextState.countyPrices = {
        ...nextState.countyPrices,
        ...payload.reduce((acc, i) => ({ ...acc, [i.county_id]: i.price / 100 }), {}),
      };
    }),
  )
  .handleAction(actions.getNicheFilesSubscription.success, (state, { payload }) =>
    produce(state, (nextState) => {
      nextState.subscription = payload || { ...emptySubscription };

      nextState.subscriptions = nextState.subscriptions.map((s) => {
        if (s.id !== payload?.id) return s;

        return {
          ...s,
          ...payload,
        };
      });
    }),
  )
  .handleAction(actions.getNicheFilesSubscriptions.success, (state, { payload }) =>
    produce(state, (nextState) => {
      nextState.subscriptionsLoaded = true;
      nextState.subscriptions = payload;
    }),
  )
  .handleAction(actions.getNicheFiles.success, (state, action) =>
    produce(state, (nextState) => {
      const { items, total } = action.payload;
      nextState.files = { list: items, total };
    }),
  )
  .handleAction(actions.getNicheFiles.failure, (state, action) =>
    produce(state, (nextState) => {
      nextState.errors[NicheFilesError.getNicheFilesError] = action.payload;
    }),
  )
  .handleAction(actions.getNicheFile.success, (state, action) =>
    produce(state, (nextState) => {
      const files = nextState.files?.list;
      const file = action.payload;

      if (files?.length) {
        const foundNicheFileIndex = files.findIndex((item) => file.id === item.id);

        files[foundNicheFileIndex] = file;

        if (nextState.files) {
          nextState.files.list = [...files];
        }
      }

      nextState.file = file;
    }),
  )
  .handleAction(actions.getNicheFile.failure, (state, action) =>
    produce(state, (nextState) => {
      nextState.errors[NicheFilesError.getNicheFileError] = action.payload;
    }),
  )
  .handleAction(actions.deleteNicheFile.success, (state, { payload }) =>
    produce(state, (nextState) => {
      const files = nextState.files?.list;
      const { id } = payload;
      if (files?.length) {
        const foundNicheFileIndex = files.findIndex((file) => file.id === id);
        if (foundNicheFileIndex > -1 && nextState.files) {
          files.splice(foundNicheFileIndex, 1);
          nextState.files.list = [...files];
        }
      }
    }),
  )
  .handleAction(actions.updateNicheFileStatus.success, (state, { payload }) =>
    produce(state, (nextState) => {
      const files = nextState.files?.list;
      const { id } = payload;
      if (files?.length) {
        const foundNicheFileIndex = files.findIndex((file) => file.id === id);
        if (foundNicheFileIndex > -1 && nextState.files) {
          files[foundNicheFileIndex] = payload;
          nextState.files.list = [...files];
        }
      }
    }),
  )
  .handleAction(actions.setFilter, (state, action) =>
    produce(state, (nextState) => {
      nextState.filter = action.payload || { ...defaultFilter };
    }),
  )
  .handleAction(actions.clearNicheFiles, (state) =>
    produce(state, (nextState) => {
      nextState.files = null;
    }),
  )
  .handleAction(actions.clearNicheFile, (state) =>
    produce(state, (nextState) => {
      nextState.file = null;
    }),
  )
  .handleAction(actions.clearErrors, (state, action) =>
    produce(state, (nextState) => {
      if (action.payload) {
        action.payload.forEach((err: NicheFilesError) => {
          nextState.errors[err] = null;
        });
      } else {
        nextState.errors = initialState.errors;
      }
    }),
  );

export { reducer as NicheFilesReducer };
